import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Receipt, Calendar, Save, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import SearchableMedicineDropdownByStock from '../../components/SearchableMedicineDropdownByStock';

const safeToFixed = (value, digits = 2) => {
    const num = Number(value);
    return Number.isFinite(num) ? Number(num.toFixed(digits)) : 0;
};

const NewInvoice = () => {
    const navigate = useNavigate();
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [medicines, setMedicines] = useState([{
        id: Date.now(),
        medicine: '',
        batchNumber: '',
        expiryDate: '',
        quantity: 0,
        basePrice: 0,
        pricePerUnit: 0,
        gstPercentage: 0,
        gstAmount: 0,
        discountPercentage: 0,
        discountAmount: 0,
        totalPrice: 0,
        availableStock: 0,
    }]);
    const [medicineList, setMedicineList] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [batchNumbers, setBatchNumbers] = useState({});
    const [isManualRound, setIsManualRound] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState('');
    const [patient] = useState(null);
    const [focusRowId, setFocusRowId] = useState(null);

    // [NEW] Kit State
    const [kits, setKits] = useState([]);
    const [selectedKit, setSelectedKit] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        patient_id: '',
        mobileNumber: '',
        village: '',
        netAmount: '',
        paymentMode: '',
        cashAmount: '',
        onlineAmount: '',
        debitsAmount: '',
        round_amount: '0',
    });
    const [errors, setErrors] = useState({});

    // Auto-fill patient details
    useEffect(() => {
        if (patient) {
            setFormData(prev => ({
                ...prev,
                name: `${patient.firstName} ${patient.lastName}`,
                mobileNumber: patient.contactNumber || patient.mobileNo || patient.phone || '',
                village: patient.village || '',
                patient_id: patient.id || '',
            }));
        }
    }, [patient]);

    // Fetch Medicines
    useEffect(() => {
        const fetchMedicines = async () => {
            try {
                const response = await axiosInstance.get('/medicine/by-stock');
                setMedicineList(response.data.medicines || []);
            } catch (error) {
                console.error('Error fetching medicines:', error);
                setMedicineList([]);
            }
        };
        const fetchKits = async () => {
            try {
                const response = await axiosInstance.get('/kit');
                setKits(response.data || []);
            } catch (error) {
                console.error('Error fetching kits:', error);
            }
        };
        fetchMedicines();
        fetchKits();
    }, []);

    const handleEnterKey = (e) => {
        if (e.key === "Enter") {
            // Prevent default to avoid activating buttons/submit
            e.preventDefault();

            // Get all focusable elements including dropdowns with tabIndex
            const focusable = document.querySelectorAll('input, select, textarea, button, [tabindex="0"]');
            const list = Array.from(focusable).filter(el => !el.disabled && el.tabIndex !== -1);
            const index = list.indexOf(e.target);

            // Move to next element (do not trigger button clicks)
            if (index > -1 && index < list.length - 1) {
                list[index + 1].focus();
            }
        }
    };

    // TOTAL AMOUNT CALCULATION – 100% ACCURATE
    useEffect(() => {
        medicines.forEach(med => {
            const quantity = med.quantity || 0;
            const basePrice = med.basePrice || 0;

            // Base amount = basePrice × quantity
            const baseAmount = safeToFixed(basePrice * quantity);

            // GST = baseAmount × (GST%/100)
            const gstAmount = safeToFixed(baseAmount * ((med.gstPercentage || 0) / 100));

            // Discount = (base + GST) × (disc%/100)
            const discountAmount = safeToFixed((baseAmount + gstAmount) * ((med.discountPercentage || 0) / 100));
        });

        const totalAmt = medicines.reduce((sum, med) => sum + (med.totalPrice || 0), 0);
        setTotalAmount(safeToFixed(totalAmt));

        if (!isManualRound) {
            // ✅ Custom rounding logic: nearest 10 based on last digit
            const lastDigit = totalAmt % 10;  // Get decimal part after tens place
            let roundedNet;

            if (lastDigit >= 5) {
                // Round up to next 10
                roundedNet = Math.ceil(totalAmt / 10) * 10;
            } else {
                // Round down to previous 10
                roundedNet = Math.floor(totalAmt / 10) * 10;
            }

            const adjustment = safeToFixed(roundedNet - totalAmt);
            setFormData(prev => ({
                ...prev,
                netAmount: roundedNet.toFixed(2),
                round_amount: adjustment.toFixed(2)
            }));
        }
    }, [medicines, isManualRound]);

    // Helper: Calculate row data from batch
    const calculateRowFromBatch = (med, batch) => {
        const pricePerUnit = parseFloat(batch.selling_price_per_unit) || 0;
        const quantity = med.quantity || 0;
        const subtotal = safeToFixed(quantity * pricePerUnit);
        const discountAmount = safeToFixed(subtotal * ((med.discountPercentage || 0) / 100));
        const totalPrice = safeToFixed(subtotal - discountAmount);

        return {
            ...med,
            batchNumber: batch.batch_number,
            pricePerUnit,
            basePrice: pricePerUnit,
            gstAmount: 0,
            discountAmount,
            totalPrice,
            availableStock: batch.available_units || 0,
            expiryDate: batch.expiry_date || '',
        };
    };

    // Fetch Batches
    // Fetch Batches
    const fetchBatches = async (medicineId, rowId) => {
        if (!medicineId) return;
        try {
            const response = await axiosInstance.get(`/stock/${medicineId}`);
            const batches = response.data.batches || [];
            setBatchNumbers(prev => ({ ...prev, [medicineId]: batches }));

            // Auto-focus and Auto-select logic
            if (rowId) {
                // Focus Batch Dropdown
                setTimeout(() => {
                    const batchSelect = document.getElementById(`batch-${rowId}`);
                    if (batchSelect) batchSelect.focus();
                }, 100);

                // Auto-select if single batch
                if (batches.length === 1) {
                    const batch = batches[0];
                    setMedicines(prev => prev.map(med => {
                        if (med.id === rowId) {
                            return calculateRowFromBatch(med, batch);
                        }
                        return med;
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching batches:', error);
        }
    };

    const handleBatchChange = (id, batchNumber, medicineId) => {
        const selectedBatch = batchNumbers[medicineId]?.find(b => b.batch_number === batchNumber);
        if (!selectedBatch) return;

        setMedicines(prev => prev.map(med => {
            if (med.id === id) {
                return calculateRowFromBatch(med, selectedBatch);
            }
            return med;
        }));
    };

    // [NEW] Handle Kit Selection
    const handleKitSelect = async (e) => {
        const kitId = e.target.value;
        setSelectedKit(kitId);
        if (!kitId) return;

        try {
            const response = await axiosInstance.get(`/kit/${kitId}/apply`);
            const kitItems = response.data.items || [];

            if (kitItems.length === 0) {
                alert("This kit has no items.");
                return;
            }

            const newMedicines = [];

            // Process each item
            for (const item of kitItems) {
                const rowId = Date.now() + Math.random();

                // Trigger batch fetch for this medicine (async, don't await blocking)
                fetchBatches(item.medicine_id, rowId);

                // Construct Row
                const newRow = {
                    id: rowId,
                    medicine: item.medicine_id,
                    batchNumber: item.batch_number || '',
                    expiryDate: item.expiry_date || '',
                    quantity: item.requested_quantity || 1,
                    basePrice: parseFloat(item.price_per_unit || 0),
                    pricePerUnit: parseFloat(item.price_per_unit || 0),
                    gstPercentage: parseFloat(item.gst_percentage || 0),
                    gstAmount: 0,
                    discountPercentage: 0,
                    discountAmount: 0,
                    totalPrice: 0,
                    availableStock: item.available_stock || 0,
                };

                // Calculate Totals immediately
                const subtotal = safeToFixed(newRow.quantity * newRow.pricePerUnit);
                // gstAmount = baseAmount * (gst% / 100) -> logic from useEffect
                // Note: basePrice and pricePerUnit are usually same or base is w/o GST?
                // In useEffect: baseAmount = basePrice * quantity. gstAmount = baseAmount * gst%.
                // Here we treat pricePerUnit as basePrice per unit.

                const gstAmount = safeToFixed(subtotal * (newRow.gstPercentage / 100));
                // No discount logic in kit apply currently (defaults to 0)
                const discountAmount = 0;

                const totalPrice = safeToFixed(subtotal + gstAmount - discountAmount);

                newRow.gstAmount = gstAmount;
                newRow.totalPrice = totalPrice;
                newRow.medicineName = item.medicine_name; // Store for defaultDisplay

                newMedicines.push(newRow);
            }

            // Append to existing medicines (filtering out empty first row if untouched)
            setMedicines(prev => {
                const filteredPrev = prev.filter(m => m.medicine); // Keep only rows with data
                return [...filteredPrev, ...newMedicines];
            });

            // Reset selection so user can pick another if they want
            setTimeout(() => setSelectedKit(''), 1000);

        } catch (error) {
            console.error("Error applying kit:", error);
            alert("Failed to apply kit.");
        }
    };

    const addMedicine = () => {
        if (medicines.some(med => !isRowValid(med))) {
            setErrors(prev => ({ ...prev, medicines: 'Please complete the current row first' }));
            return;
        }
        const newId = Date.now() + Math.random();
        const newMedicine = {
            id: newId,
            medicine: '',
            batchNumber: '',
            expiryDate: '',
            quantity: 0,
            basePrice: 0,
            pricePerUnit: 0,
            gstPercentage: 0,
            gstAmount: 0,
            discountPercentage: 0,
            discountAmount: 0,
            totalPrice: 0,
            availableStock: 0,
        };
        setMedicines([...medicines, newMedicine]);
        setFocusRowId(newId);
        setErrors(prev => ({ ...prev, medicines: '' }));
    };

    const handleMedicineChange = (id, field, value) => {
        setMedicines(prev => prev.map(med => {
            if (med.id === id) {
                const updatedMed = { ...med, [field]: value };
                if (field === 'medicine') {
                    const medicineId = typeof value === 'object' ? value.id : value;
                    fetchBatches(medicineId, med.id); // Pass med.id as rowId
                    const selectedMedicine = typeof value === 'object' ? value : medicineList.find(m => m.id === parseInt(value));
                    Object.assign(updatedMed, {
                        medicine: medicineId,
                        batchNumber: '',
                        basePrice: 0,
                        pricePerUnit: 0,
                        gstPercentage: selectedMedicine?.gst_percentage || 0,
                        gstAmount: 0,
                        discountPercentage: 0,
                        discountAmount: 0,
                        availableStock: 0,
                        expiryDate: '',
                        totalPrice: 0,
                    });
                }
                if (['quantity', 'discountPercentage'].includes(field)) {
                    const quantity = field === 'quantity' ? value : updatedMed.quantity;
                    const pricePerUnit = updatedMed.pricePerUnit || 0;

                    let subtotal = safeToFixed(quantity * pricePerUnit);
                    let discountAmount = 0;

                    if (updatedMed.discountPercentage > 0) {
                        discountAmount = safeToFixed(subtotal * (updatedMed.discountPercentage / 100));
                    }

                    const totalPrice = safeToFixed(subtotal - discountAmount);

                    updatedMed.gstAmount = 0;
                    updatedMed.discountAmount = discountAmount;
                    updatedMed.totalPrice = totalPrice;
                }
                return updatedMed;
            }
            return med;
        }));
    };

    const removeMedicine = (id) => {
        setMedicines(medicines.filter(med => med.id !== id));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'round_amount') {
            setIsManualRound(true);
            const adj = parseFloat(value) || 0;
            const newNetAmount = (totalAmount + adj).toFixed(2);
            setFormData(prev => ({ ...prev, round_amount: value, netAmount: newNetAmount }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (name === 'paymentMode') {
            setFormData(prev => ({ ...prev, cashAmount: '', onlineAmount: '', debitsAmount: '' }));
        }
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Name is required';
        if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
            newErrors.mobileNumber = 'Mobile Number must be 10 digits';
        }
        if (!formData.paymentMode) newErrors.paymentMode = 'Payment Mode is required';
        if (!formData.netAmount || parseFloat(formData.netAmount) <= 0) {
            newErrors.netAmount = 'Net Amount must be positive';
        }
        if (formData.paymentMode === 'cash_and_online') {
            if (!formData.cashAmount || parseFloat(formData.cashAmount) < 0) newErrors.cashAmount = 'Cash Amount required';
            if (!formData.onlineAmount || parseFloat(formData.onlineAmount) < 0) newErrors.onlineAmount = 'Online Amount required';
        }
        if (formData.paymentMode === 'debits') {
            if (!formData.debitsAmount || parseFloat(formData.debitsAmount) <= 0) newErrors.debitsAmount = 'Debits Amount required';
        }
        medicines.forEach((med, i) => {
            if (med.quantity > med.availableStock) {
                newErrors[`medicine_${i}`] = `Qty exceeds stock (${med.availableStock})`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isRowValid = (med) => {
        return med.medicine && med.batchNumber && med.quantity > 0 && med.pricePerUnit > 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm() || !medicines.every(isRowValid)) return;

        const invoiceData = {
            patient_id: patient?.id || null,
            village: formData.village || formData.mobileNumber,
            name: formData.name,
            mobile_number: formData.mobileNumber,
            doctor: selectedDoctor || null,
            invoice_date: invoiceDate,
            total_amount: totalAmount,
            discount: 0,
            tax: 0,
            round_amount: parseFloat(formData.round_amount) || 0,
            net_amount: parseFloat(formData.netAmount),
            payment_mode: formData.paymentMode,
            cash_amount: formData.paymentMode === 'cash_and_online' ? parseFloat(formData.cashAmount) : null,
            online_amount: formData.paymentMode === 'cash_and_online' ? parseFloat(formData.onlineAmount) : null,
            debits_amount: formData.paymentMode === 'debits' ? parseFloat(formData.debitsAmount) : null,
            remark: formData.paymentMode === 'debits' ? 'baki' : '',
            items: medicines.map(med => ({
                medicine_id: parseInt(med.medicine),
                batch_number: med.batchNumber,
                quantity: parseInt(med.quantity),
                base_price: safeToFixed(med.basePrice),
                price_per_unit: safeToFixed(med.pricePerUnit),
                gst_percentage: safeToFixed(med.gstPercentage),
                gst_amount: safeToFixed(med.gstAmount),
                discount_percentage: safeToFixed(med.discountPercentage),
                discount_amount: safeToFixed(med.discountAmount),
                total_price: safeToFixed(med.totalPrice),
            })),
        };

        try {
            await axiosInstance.post('/invoice', invoiceData);
            alert('Invoice saved successfully!');
            navigate('/invoice');
        } catch (error) {
            setErrors(prev => ({
                ...prev,
                submit: error.response?.data?.message || 'Failed to save invoice'
            }));
        }
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-4 px-3 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">

                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 border border-teal-100">
                        <div className="bg-teal-600 p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-xl">
                                        <Receipt className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                                    </div>
                                    <div>
                                        <h1 className="text-xl sm:text-2xl font-bold text-white">New Invoice</h1>
                                        <p className="text-teal-100 text-xs sm:text-sm mt-1">Create a new sale invoice</p>
                                    </div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/30 shadow-inner">
                                    <label className="text-xs text-teal-100 font-medium">Invoice Date</label>
                                    <div className="relative mt-2">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white pointer-events-none" />
                                        <input
                                            type="date"
                                            name="invoiceDate"
                                            value={invoiceDate}
                                            onChange={(e) => setInvoiceDate(e.target.value)}
                                            className="
        w-full bg-transparent text-white font-semibold text-sm 
        pl-10 pr-4 py-2 
        rounded-lg border border-white/30 outline-none cursor-pointer
        focus:border-white focus:shadow-lg focus:shadow-teal-400/30
        transition-all duration-300
      "
                                        />
                                    </div>
                                </div>

                                {/* [NEW] Kit Selection */}
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/30 shadow-inner min-w-[200px]">
                                    <label className="text-xs text-teal-100 font-medium">Load Kit</label>
                                    <div className="relative mt-2">
                                        <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white pointer-events-none" />
                                        <select
                                            value={selectedKit}
                                            onChange={handleKitSelect}
                                            className="
                                                w-full bg-transparent text-white font-semibold text-sm 
                                                pl-10 pr-4 py-2 
                                                rounded-lg border border-white/30 outline-none cursor-pointer
                                                focus:border-white focus:shadow-lg focus:shadow-teal-400/30
                                                transition-all duration-300
                                                [&>option]:text-gray-900
                                            "
                                        >
                                            <option value="">Select Kit</option>
                                            {kits.map(kit => (
                                                <option key={kit.id} value={kit.id}>{kit.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">

                        {errors.submit && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                                <p className="text-red-700 text-sm font-medium">{errors.submit}</p>
                            </div>
                        )}

                        {/* Customer Details */}
                        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-teal-200">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <div className="w-1 h-6 bg-teal-600 rounded-full mr-3"></div>
                                Customer Details
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-teal-400 transition-all"
                                        placeholder="Customer name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        onKeyDown={handleEnterKey}
                                    />
                                    {errors.name && <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number</label>
                                    <input
                                        type="tel"
                                        name="mobileNumber"
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-teal-400 transition-all"
                                        placeholder="10-digit number"
                                        value={formData.mobileNumber}
                                        onChange={handleChange}
                                        onKeyDown={handleEnterKey}
                                    />
                                    {errors.mobileNumber && <p className="text-red-500 text-xs mt-1.5">{errors.mobileNumber}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Village Name</label>
                                    <input
                                        type="text"
                                        name="village"
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-teal-400 transition-all"
                                        placeholder="Enter Village Name"
                                        value={formData.village}
                                        onChange={handleChange}
                                        onKeyDown={handleEnterKey}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Doctor</label>
                                    <select
                                        value={selectedDoctor}
                                        onChange={(e) => setSelectedDoctor(e.target.value)}
                                        onKeyDown={handleEnterKey}
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                                    >
                                        <option value="">Select Doctor</option>
                                        <option value="Dr. Ashraf Sherasiya">Dr. Ashraf Sherasiya</option>
                                        <option value="Dr. Rozmeen Sherasiya">Dr. Rozmeen Sherasiya</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Medicines Section */}
                        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-teal-200">
                            <div className="mb-4">
                                <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center">
                                    <div className="w-1 h-6 bg-teal-600 rounded-full mr-3"></div>
                                    Medicines
                                </h2>
                            </div>

                            {errors.medicines && (
                                <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-lg mb-4">
                                    <p className="text-red-700 text-sm">{errors.medicines}</p>
                                </div>
                            )}

                            {/* Desktop View */}
                            <div className="hidden lg:block space-y-5">
                                {medicines.map((med, index) => {
                                    const medicineName = medicineList.find(m => m.id === parseInt(med.medicine))?.name || 'Select Medicine';
                                    const isComplete = med.medicine && med.batchNumber && med.quantity > 0 && med.pricePerUnit > 0;

                                    return (
                                        <div
                                            key={med.id}
                                            className={`relative bg-white rounded-2xl shadow-xl border-2 p-6 transition-all duration-300 hover:shadow-2xl ${isComplete ? 'border-teal-400 ring-2 ring-teal-100 ring-opacity-50' : 'border-gray-200'}`}
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-teal-500 text-white font-bold rounded-full flex items-center justify-center shadow-md">
                                                        {index + 1}
                                                    </div>
                                                    <h3 className="text-lg font-bold text-gray-800">{medicineName}</h3>
                                                    {isComplete && (
                                                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full animate-pulse">
                                                            Ready
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeMedicine(med.id)}
                                                    onKeyDown={handleEnterKey}
                                                    className="p-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all transform hover:scale-110"
                                                >
                                                    <Trash2 className="h-5 w-5" />
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 text-sm">
                                                <div className="xl:col-span-1">
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Medicine *</label>
                                                    <SearchableMedicineDropdownByStock
                                                        value={med.medicine}
                                                        onChange={(medicine) => handleMedicineChange(med.id, 'medicine', medicine)}
                                                        onKeyDown={handleEnterKey}
                                                        autoFocus={med.id === focusRowId}
                                                        placeholder="Search medicine..."
                                                        className="w-full"
                                                        defaultDisplay={med.medicineName}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Batch No.</label>
                                                    <select
                                                        id={`batch-${med.id}`} // Added ID for focus
                                                        value={med.batchNumber}
                                                        onChange={(e) => handleBatchChange(med.id, e.target.value, med.medicine)}
                                                        onKeyDown={handleEnterKey}
                                                        disabled={!med.medicine}
                                                        className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
                                                    >
                                                        <option value="">Select Batch</option>
                                                        {batchNumbers[med.medicine]?.map(batch => (
                                                            <option key={batch.batch_number} value={batch.batch_number}>
                                                                {batch.batch_number} (₹{batch.selling_price_per_unit}, Stock: {batch.available_units})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Expiry</label>
                                                    <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 font-medium">
                                                        {med.expiryDate ? new Date(med.expiryDate).toLocaleDateString('en-IN') : '-'}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Stock</label>
                                                    <div className="px-3 py-2.5 bg-teal-100 text-teal-700 font-bold rounded-xl text-center">
                                                        {med.availableStock || 0}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Qty *</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={med.quantity}
                                                        onChange={(e) => handleMedicineChange(med.id, 'quantity', parseInt(e.target.value) || 0)}
                                                        onKeyDown={handleEnterKey}
                                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                                    />
                                                    {errors[`medicine_${index}`] && <p className="text-xs text-red-500 mt-1">{errors[`medicine_${index}`]}</p>}
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Price/Unit</label>
                                                    <div className="px-3 py-2.5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl font-bold text-emerald-700">
                                                        ₹{parseFloat(med.pricePerUnit || 0).toFixed(2)}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">GST %</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={med.gstPercentage}
                                                        onChange={(e) => handleMedicineChange(med.id, 'gstPercentage', parseFloat(e.target.value) || 0)}
                                                        onKeyDown={handleEnterKey}
                                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">GST ₹</label>
                                                    <div className="px-3 py-2.5 bg-teal-100 text-teal-700 font-bold rounded-xl">
                                                        ₹{(med.gstAmount || 0).toFixed(2)}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Disc %</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={med.discountPercentage}
                                                        onChange={(e) => handleMedicineChange(med.id, 'discountPercentage', parseFloat(e.target.value) || 0)}
                                                        onKeyDown={handleEnterKey}
                                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Disc ₹</label>
                                                    <div className="px-3 py-2.5 bg-orange-100 text-orange-700 font-bold rounded-xl">
                                                        ₹{(med.discountAmount || 0).toFixed(2)}
                                                    </div>
                                                </div>

                                                <div className="xl:col-span-2">
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Total Price</label>
                                                    <div className="px-5 py-3 bg-teal-600 text-white font-bold text-lg rounded-xl text-center shadow-md">
                                                        ₹{(med.totalPrice || 0).toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>

                                            {!isComplete && (
                                                <div className="mt-4 flex items-center gap-2 text-amber-600 text-xs">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    <span>Please complete all fields</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={addMedicine}
                                        className="flex items-center space-x-2 px-4 py-3.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                    >
                                        <Plus className="h-5 w-5" />
                                        <span className="font-semibold">Add Medicine</span>
                                    </button>
                                </div>
                            </div>

                            {/* Mobile View */}
                            <div className="lg:hidden space-y-6">
                                {medicines.map((med, index) => {
                                    const medicineName = medicineList.find(m => m.id === parseInt(med.medicine))?.name || 'Select Medicine';
                                    const isComplete = med.medicine && med.batchNumber && med.quantity > 0 && med.pricePerUnit > 0;

                                    return (
                                        <div key={med.id} className={`rounded-2xl border-2 p-5 shadow-xl transition-all duration-300 ${isComplete ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-white ring-2 ring-teal-100' : 'border-gray-300 bg-white'}`}>
                                            <div className="flex justify-between items-center mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md">
                                                        {index + 1}
                                                    </div>
                                                    <h3 className="font-bold text-gray-800 text-base truncate max-w-[180px]">{medicineName}</h3>
                                                    {isComplete && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">Ready</span>}
                                                </div>
                                                <button type="button" onClick={() => removeMedicine(med.id)} onKeyDown={handleEnterKey} className="p-2 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-all">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 gap-4 text-sm">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">Medicine *</label>
                                                    <SearchableMedicineDropdownByStock
                                                        value={med.medicine}
                                                        onChange={(medicine) => handleMedicineChange(med.id, 'medicine', medicine)}
                                                        onKeyDown={handleEnterKey}
                                                        placeholder="Search medicine..."
                                                        className="w-full"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">Batch No. *</label>
                                                    <select
                                                        value={med.batchNumber}
                                                        onChange={(e) => handleBatchChange(med.id, e.target.value, med.medicine)}
                                                        onKeyDown={handleEnterKey}
                                                        disabled={!med.medicine}
                                                        className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                                    >
                                                        <option value="">Select Batch</option>
                                                        {batchNumbers[med.medicine]?.map(batch => (
                                                            <option key={batch.batch_number} value={batch.batch_number}>
                                                                {batch.batch_number} (₹{batch.selling_price_per_unit}, Stock: {batch.available_units})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-700 mb-1">Expiry</label>
                                                        <div className="px-3 py-2.5 bg-gray-100 border border-gray-300 rounded-xl text-gray-700 font-medium text-center">
                                                            {med.expiryDate ? new Date(med.expiryDate).toLocaleDateString('en-IN') : '-'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-700 mb-1">Stock</label>
                                                        <div className="px-3 py-2.5 bg-teal-100 text-teal-700 font-bold rounded-xl text-center">
                                                            {med.availableStock || 0}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">Quantity *</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={med.quantity}
                                                        onChange={(e) => handleMedicineChange(med.id, 'quantity', parseInt(e.target.value) || 0)}
                                                        onKeyDown={handleEnterKey}
                                                        className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                                                    />
                                                    {errors[`medicine_${index}`] && <p className="text-xs text-red-500 mt-1">{errors[`medicine_${index}`]}</p>}
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">Price/Unit</label>
                                                    <div className="px-3 py-2.5 bg-emerald-100 text-emerald-700 font-bold rounded-xl text-center">
                                                        ₹{parseFloat(med.pricePerUnit || 0).toFixed(2)}
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-700 mb-1">GST %</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={med.gstPercentage}
                                                            onChange={(e) => handleMedicineChange(med.id, 'gstPercentage', parseFloat(e.target.value) || 0)}
                                                            onKeyDown={handleEnterKey}
                                                            className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-700 mb-1">GST ₹</label>
                                                        <div className="px-3 py-2.5 bg-teal-100 text-teal-700 font-bold rounded-xl text-center">
                                                            ₹{(med.gstAmount || 0).toFixed(2)}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-700 mb-1">Disc %</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={med.discountPercentage}
                                                            onChange={(e) => handleMedicineChange(med.id, 'discountPercentage', parseFloat(e.target.value) || 0)}
                                                            onKeyDown={handleEnterKey}
                                                            className="w-full px-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-700 mb-1">Disc ₹</label>
                                                        <div className="px-3 py-2.5 bg-orange-100 text-orange-700 font-bold rounded-xl text-center">
                                                            ₹{(med.discountAmount || 0).toFixed(2)}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="pt-3 border-t-2 border-dashed border-teal-300 mt-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-bold text-gray-800">Total Price</span>
                                                        <span className="text-xl font-bold text-teal-600">
                                                            ₹{(med.totalPrice || 0).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {!isComplete && (
                                                    <div className="mt-3 p-2 bg-amber-50 border border-amber-300 rounded-lg flex items-center gap-2 text-amber-700 text-xs">
                                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                        <span>Please fill all required fields</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={addMedicine}
                                        onKeyDown={handleEnterKey}
                                        className="flex items-center space-x-2 px-4 py-3.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                    >
                                        <Plus className="h-5 w-5" />
                                        <span className="font-semibold">Add Medicine</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Payment & Summary */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-teal-200">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                    <div className="w-1 h-6 bg-teal-600 rounded-full mr-3"></div>
                                    Payment Details
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Mode *</label>
                                        <select
                                            name="paymentMode"
                                            onKeyDown={handleEnterKey}
                                            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                                            value={formData.paymentMode}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Mode</option>
                                            <option value="cash">Cash</option>
                                            <option value="online">Online</option>
                                            <option value="cash_and_online">Cash + Online</option>
                                            <option value="card">Card</option>
                                            <option value="debits">Debits</option>
                                        </select>
                                        {errors.paymentMode && <p className="text-red-500 text-xs mt-1.5">{errors.paymentMode}</p>}
                                    </div>
                                    {formData.paymentMode === 'cash_and_online' && (
                                        <div className="space-y-4 bg-teal-50/50 p-4 rounded-xl border border-teal-100">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Cash Amount</label>
                                                <input
                                                    type="number"
                                                    name="cashAmount"
                                                    onKeyDown={handleEnterKey}
                                                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                                                    value={formData.cashAmount}
                                                    onChange={handleChange}
                                                />
                                                {errors.cashAmount && <p className="text-red-500 text-xs mt-1.5">{errors.cashAmount}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Online Amount</label>
                                                <input
                                                    type="number"
                                                    name="onlineAmount"
                                                    onKeyDown={handleEnterKey}
                                                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                                                    value={formData.onlineAmount}
                                                    onChange={handleChange}
                                                />
                                                {errors.onlineAmount && <p className="text-red-500 text-xs mt-1.5">{errors.onlineAmount}</p>}
                                            </div>
                                        </div>
                                    )}
                                    {formData.paymentMode === 'debits' && (
                                        <div className="bg-orange-50/50 p-4 rounded-xl border border-orange-100">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Debits Amount</label>
                                            <input
                                                type="number"
                                                name="debitsAmount"
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                                                value={formData.debitsAmount}
                                                onChange={handleChange}
                                            />
                                            {errors.debitsAmount && <p className="text-red-500 text-xs mt-1.5">{errors.debitsAmount}</p>}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-teal-100 rounded-2xl shadow-lg p-4 sm:p-6 border-2 border-teal-200">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                    <div className="w-1 h-6 bg-teal-600 rounded-full mr-3"></div>
                                    Amount Summary
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                                        <span className="text-gray-600 font-medium">Subtotal (Before GST)</span>
                                        <span className="font-bold text-gray-800">
                                            ₹{medicines.reduce((s, m) => s + ((m.basePrice || 0) * (m.quantity || 0)), 0).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                                        <span className="text-teal-600 font-medium text-sm">Total GST</span>
                                        <span className="text-teal-700 font-bold">+₹{medicines.reduce((s, m) => s + (m.gstAmount || 0), 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                                        <span className="text-orange-600 font-medium text-sm">Total Discount</span>
                                        <span className="text-orange-700 font-bold">-₹{medicines.reduce((s, m) => s + (m.discountAmount || 0), 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-white rounded-xl border-2 border-teal-200">
                                        <span className="text-gray-700 font-semibold">Total Amount</span>
                                        <span className="text-teal-700 font-bold text-lg">₹{totalAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="pt-3 border-t-2 border-teal-200">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Rounded Adjustment (+/-)
                                        </label>
                                        <input
                                            type="number"
                                            name="round_amount"
                                            placeholder="0"
                                            step="0.01"
                                            onKeyDown={handleEnterKey}
                                            className="w-full px-4 py-2.5 border-2 border-amber-300 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50 font-medium text-amber-800"
                                            value={formData.round_amount}
                                            onChange={handleChange}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">e.g. +5 to make ₹60, -3 to make ₹50</p>
                                    </div>
                                    <div className="pt-3">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Net Amount *</label>
                                        <div className="w-full px-4 py-3 bg-teal-600 text-white font-bold text-xl rounded-xl text-center shadow-md">
                                            ₹{formData.netAmount || '0.00'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Save Button */}
                        {/* <div className="flex justify-end mt-8 space-x-4">
                            <span>Cancel</span>
                            <button
                                type="submit"
                                className="flex items-center space-x-2 px-8 py-3.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                                disabled={!medicines.length || !medicines.every(isRowValid)}
                            >
                                <Save className="h-5 w-5" />
                                <span>Save Invoice</span>
                            </button>
                        </div> */}
                        <div className="flex justify-end mt-8 space-x-4">
                            <button
                                type="button"
                                onClick={() => navigate('/invoice')}
                                className="flex items-center space-x-2 px-8 py-3.5 bg-gray-500 text-white rounded-xl hover:bg-red-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-bold"
                            >
                                <span>Cancel</span>
                            </button>
                            <button
                                type="submit"
                                className="flex items-center space-x-2 px-8 py-3.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                                disabled={!medicines.length || !medicines.every(isRowValid)}
                            >
                                <Save className="h-5 w-5" />
                                <span>Save Invoice</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Authenticated>
    );
};

export default NewInvoice;